import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp, Trash2, AlertCircle, Lock, Eye, EyeOff, Info, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PRINT_METHODS, SIZE_TIERS, RETAIL_SIZE_TIERS, BLOCKERS, PRINT_COLOURS, SCREEN_PRINT_TYPES, SHEET_SIZES, formatCurrency, API } from "./constants";
import { PrintSizingSection } from "./PrintSizingSection";
import { usePlacements } from "./printSizing";

import { useTspConfig } from "./useTspConfig";
import MethodInputFields from "./MethodInputFields";

export function ArtworkGroupCard({ 
  artwork, 
  index, 
  onUpdate, 
  onUpdateFields,
  onRemove, 
  onUpdateAllocation, 
  garments, 
  jobId,
  breakdown, 
  showInternal,
  clientType = "Business"  // "Retail" uses clean size tiers
}) {
  const [expanded, setExpanded] = useState(true);
  const { placements, addPlacement } = usePlacements();
  const [addingPlacement, setAddingPlacement] = useState(false);
  const [customPlacement, setCustomPlacement] = useState("");

  // Initialise Traditional Screen Print defaults (Film + Screen selected, 1 colour)
  useEffect(() => {
    if (artwork.print_method === "Traditional Screen Print") {
      const patch = {};
      if (artwork.tsp_selected_charges === undefined || artwork.tsp_selected_charges === null) {
        patch.tsp_selected_charges = ["Film", "Screen"];
      }
      if (!artwork.tsp_colours) patch.tsp_colours = 1;
      if (Object.keys(patch).length > 0) {
        (onUpdateFields || ((f) => Object.entries(f).forEach(([k, v]) => onUpdate(k, v))))(patch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artwork.print_method]);
  
  // Determine if we're in retail mode
  const isRetailMode = clientType === "Retail";
  const tspConfig = useTspConfig(artwork.print_method === "Traditional Screen Print");
  
  // Calculate total placements from allocations
  const totalPlacements = (artwork.allocations || []).reduce((sum, alloc) => {
    const garment = garments.find((g) => g.id === alloc.garment_id);
    if (garment && alloc.placement_count > 0) {
      return sum + (garment.quantity * alloc.placement_count);
    }
    return sum;
  }, 0);

  return (
    <div
      className="border rounded-lg overflow-hidden"
      data-testid={`artwork-group-${index}`}
    >
      {/* Header */}
      <div
        className="p-3 bg-secondary/30 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {/* Hide method badge in retail mode */}
          {!isRetailMode && (
            <Badge variant="outline" className={`method-badge ${!artwork.print_method ? 'text-muted-foreground border-dashed' : ''}`}>
              {artwork.print_method || "Select Method"}
            </Badge>
          )}
          <span className="text-sm font-medium">
            {artwork.placement_name || artwork.name || artwork.override_label || `Print ${index + 1}`}
          </span>
          {/* Show size tier badge in retail mode */}
          {isRetailMode && artwork.size_tier && (
            <Badge variant="secondary" className="text-xs">
              {artwork.size_tier}
            </Badge>
          )}
          {totalPlacements > 0 && (
            <Badge variant="secondary" className="font-mono text-xs">
              {totalPlacements} placements
            </Badge>
          )}
          {/* Show pricing info only for Business mode */}
          {!isRetailMode && (
            <>
              {artwork.print_method === "Manual Override" && artwork.override_sell_ex_gst > 0 ? (
                <span className="font-mono text-sm text-green-600" title="Sell price (ex GST)">
                  {formatCurrency(artwork.override_sell_ex_gst)}
                </span>
              ) : breakdown?.cost > 0 ? (
                <span className="font-mono text-sm text-accent">
                  {formatCurrency(breakdown.cost)}
                </span>
              ) : null}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {breakdown?.error && (
            <AlertCircle className="w-4 h-4 text-destructive" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            data-testid={`remove-artwork-${index}`}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Common Fields - Simplified for Retail */}
          <div className={`grid gap-3 ${isRetailMode ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}>
            {/* Print Method - Hidden for Retail */}
            {!isRetailMode && (
              <div className="space-y-1">
                <Label className="text-xs">Print Method</Label>
                <Select
                  value={artwork.print_method || ""}
                  onValueChange={(v) => onUpdate("print_method", v)}
                >
                  <SelectTrigger data-testid={`artwork-method-${index}`}>
                    <SelectValue placeholder="Select method..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PRINT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Placement Name</Label>
              {addingPlacement ? (
                <div className="flex items-center gap-1">
                  <Input
                    autoFocus
                    placeholder="New placement name..."
                    value={customPlacement}
                    onChange={(e) => setCustomPlacement(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter" && customPlacement.trim()) {
                        const item = await addPlacement(customPlacement.trim());
                        onUpdate("placement_name", item.name);
                        setCustomPlacement("");
                        setAddingPlacement(false);
                      } else if (e.key === "Escape") {
                        setAddingPlacement(false);
                        setCustomPlacement("");
                      }
                    }}
                    data-testid={`artwork-placement-custom-${index}`}
                  />
                  <Button
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    disabled={!customPlacement.trim()}
                    onClick={async () => {
                      const item = await addPlacement(customPlacement.trim());
                      onUpdate("placement_name", item.name);
                      setCustomPlacement("");
                      setAddingPlacement(false);
                    }}
                    data-testid={`artwork-placement-add-${index}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Select
                  value={artwork.placement_name || ""}
                  onValueChange={(v) => {
                    if (v === "__other__") {
                      setAddingPlacement(true);
                    } else {
                      onUpdate("placement_name", v);
                    }
                  }}
                >
                  <SelectTrigger data-testid={`artwork-placement-${index}`}>
                    <SelectValue placeholder="Select placement..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Preserve legacy free-text value if not in the curated list */}
                    {artwork.placement_name &&
                      !placements.some((p) => p.name === artwork.placement_name) && (
                        <SelectItem value={artwork.placement_name}>
                          {artwork.placement_name}
                        </SelectItem>
                      )}
                    {placements.map((p) => (
                      <SelectItem key={p.id || p.name} value={p.name}>
                        {p.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__other__" className="text-blue-600">
                      + Other (add new)…
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {/* Size Tier - For retail: always show (no price hints). For business: only for UltraColour methods */}
            {(isRetailMode || artwork.print_method === "UltraColour Max" || artwork.print_method === "UltraColour Pro") && artwork.print_method !== "Manual Override" && (
              <div className="space-y-1">
                <Label className="text-xs">Size Tier</Label>
                <Select
                  value={artwork.size_tier || ""}
                  onValueChange={(v) => onUpdate("size_tier", v)}
                >
                  <SelectTrigger data-testid={`artwork-size-${index}`}>
                    <SelectValue placeholder="Select size..." />
                  </SelectTrigger>
                  <SelectContent>
                    {isRetailMode ? (
                      // Clean retail size tiers - NO price hints (pricing in Quote Summary)
                      RETAIL_SIZE_TIERS.map((tier) => (
                        <SelectItem key={tier.value} value={tier.value}>
                          {tier.label}
                        </SelectItem>
                      ))
                    ) : (
                      // Business size tiers with area descriptions
                      SIZE_TIERS.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Applies To Garments Section */}
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Applies To Garments
              </Label>
              <span className="text-xs text-muted-foreground font-mono">
                Total: {totalPlacements} placements
              </span>
            </div>
            
            {garments.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                Add garments first to allocate this artwork
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground mb-2">
                  Enter 0 to exclude a garment line from this artwork.
                </p>
                {garments.map((garment, gIndex) => {
                  const allocation = (artwork.allocations || []).find(
                    (a) => a.garment_id === garment.id
                  );
                  const placementCount = allocation?.placement_count || 0;
                  const linePlacements = garment.quantity * placementCount;
                  
                  return (
                    <div
                      key={garment.id}
                      className="flex items-center justify-between p-2 bg-secondary/20 rounded"
                      data-testid={`artwork-${index}-garment-${gIndex}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {garment.name || `Garment ${gIndex + 1}`}
                          {garment.colour && <span className="text-muted-foreground"> – {garment.colour}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {garment.quantity} units × {placementCount} = {linePlacements} placements
                        </p>
                      </div>
                      <div className="flex items-center gap-1 ml-3">
                        {/* Quick buttons for 0, 1, 2 */}
                        {[0, 1, 2].map((val) => (
                          <Button
                            key={val}
                            variant={placementCount === val ? "default" : "outline"}
                            size="sm"
                            className="w-8 h-8 p-0 font-mono text-xs"
                            onClick={() => onUpdateAllocation(artwork.id, garment.id, val)}
                            data-testid={`artwork-${index}-garment-${gIndex}-btn-${val}`}
                          >
                            {val}
                          </Button>
                        ))}
                        {/* Manual input for higher numbers */}
                        <Input
                          type="number"
                          min="0"
                          max="99"
                          value={placementCount}
                          onChange={(e) =>
                            onUpdateAllocation(
                              artwork.id,
                              garment.id,
                              parseInt(e.target.value) || 0
                            )
                          }
                          className="w-14 h-8 text-center font-mono text-xs"
                          data-testid={`artwork-${index}-garment-${gIndex}-input`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Print Sizing (physical dimensions + reference library) */}
          <PrintSizingSection
            artwork={artwork}
            index={index}
            garments={garments}
            jobId={jobId}
            onUpdate={onUpdate}
            onUpdateFields={onUpdateFields || ((fields) => Object.entries(fields).forEach(([k, v]) => onUpdate(k, v)))}
          />


          {/* Method-Specific Fields — single source (also used by the comparison panel) */}
          <MethodInputFields
            method={artwork.print_method}
            group={artwork}
            onUpdate={onUpdate}
            onUpdateFields={onUpdateFields}
            index={index}
            breakdown={breakdown}
            totalUnits={totalPlacements}
            context="primary"
          />
          {false && artwork.print_method === "Screen Print Pro" && (
            <div className="pt-3 border-t space-y-4">
              {/* Sheet Setup Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                  </svg>
                  <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                    Sheet Setup
                  </span>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {/* Garments per Sheet */}
                  <div className="space-y-1.5 col-span-2 sm:col-span-1">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">Garments per Sheet</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs p-3">
                            <p className="text-xs font-medium mb-1">Gang Sheet Logic</p>
                            <p className="text-xs text-muted-foreground">
                              If one sheet contains both a pocket and back print for one garment, enter <strong>1</strong>.
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              If one sheet contains 15 pocket prints, enter <strong>15</strong>.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={artwork.prints_per_sheet || 1}
                      onChange={(e) =>
                        onUpdate("prints_per_sheet", Math.max(1, parseInt(e.target.value) || 1))
                      }
                      className="h-9 font-mono"
                      data-testid={`spp-garments-per-sheet-${index}`}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      How many garments can be produced from one A3 sheet?
                    </p>
                  </div>

                  {/* Colours */}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Colours</Label>
                    <Input
                      type="number"
                      min="1"
                      max="6"
                      value={artwork.screens || 1}
                      onChange={(e) => {
                        const val = Math.min(6, Math.max(1, parseInt(e.target.value) || 1));
                        onUpdate("screens", val);
                        if (artwork.custom_colour_count > val) {
                          onUpdate("custom_colour_count", val);
                        }
                      }}
                      className="h-9 font-mono"
                      data-testid={`spp-colour-count-${index}`}
                    />
                    <p className="text-[11px] text-muted-foreground">1-6 colours</p>
                  </div>

                  {/* Sheet Size */}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Sheet Size</Label>
                    <Select
                      value={artwork.sheet_size || "Standard"}
                      onValueChange={(v) => onUpdate("sheet_size", v)}
                    >
                      <SelectTrigger className="h-9" data-testid={`spp-sheet-size-${index}`}>
                        <SelectValue placeholder="Sheet size" />
                      </SelectTrigger>
                      <SelectContent>
                        {SHEET_SIZES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">A3 gang sheet tier</p>
                  </div>
                </div>

                {/* Toggles Row */}
                <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`spp-light-dark-${index}`}
                      checked={artwork.light_on_dark || false}
                      onCheckedChange={(checked) => onUpdate("light_on_dark", checked)}
                      data-testid={`spp-light-dark-${index}`}
                    />
                    <Label htmlFor={`spp-light-dark-${index}`} className="text-sm cursor-pointer">
                      Light on Dark
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Adds +1 colour for white underbase</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>

              {/* Extras Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="13.5" cy="6.5" r="2.5" /><circle cx="19" cy="17" r="2" />
                    <circle cx="9" cy="16" r="3" /><path d="M12 2v2M2 12h2M22 12h-2M12 22v-2" />
                  </svg>
                  <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                    Extras
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Custom Colours */}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Custom Colours</Label>
                    <Input
                      type="number"
                      min="0"
                      max="4"
                      value={artwork.custom_colour_count || 0}
                      onChange={(e) => onUpdate("custom_colour_count", Math.max(0, parseInt(e.target.value) || 0))}
                      className="h-9 font-mono"
                      data-testid={`spp-custom-colours-${index}`}
                    />
                    <p className="text-[11px] text-muted-foreground">$60 per custom/Pantone colour</p>
                  </div>

                  {/* Cuts per Sheet */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">Cuts per Sheet</Label>
                      <svg className="w-3 h-3 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
                        <line x1="20" y1="4" x2="8.12" y2="15.88" /><line x1="14.47" y1="14.48" x2="20" y2="20" />
                        <line x1="8.12" y1="8.12" x2="12" y2="12" />
                      </svg>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      value={artwork.cuts_per_sheet || 0}
                      onChange={(e) => onUpdate("cuts_per_sheet", Math.max(0, parseInt(e.target.value) || 0))}
                      className="h-9 font-mono"
                      data-testid={`spp-cuts-per-sheet-${index}`}
                    />
                    <p className="text-[11px] text-muted-foreground">How many guillotine cuts per A3 sheet?</p>
                  </div>
                </div>
              </div>

              {/* Cost Breakdown Section - Always visible for Screen Print Pro */}
              {breakdown && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                    <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                      Cost Breakdown
                    </span>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Total Units:</span>
                        <span className="font-mono">{breakdown.total_placements || totalPlacements}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Sheets Required:</span>
                        <span className="font-mono font-medium">{breakdown.sheets_required || Math.ceil((breakdown.total_placements || totalPlacements) / (artwork.prints_per_sheet || 1))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Effective Colours:</span>
                        <span className="font-mono">{breakdown.effective_colours || (artwork.light_on_dark ? (artwork.screens || 1) + 1 : artwork.screens || 1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Sheet Tier:</span>
                        <span className="font-mono text-xs">{breakdown.sheet_tier || "N/A"}</span>
                      </div>
                    </div>
                    <div className="border-t pt-2 mt-2 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Sheet Cost:</span>
                        <span className="font-mono">{formatCurrency(breakdown.print_cost || 0)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Setup Cost:</span>
                        <span className="font-mono">{formatCurrency(breakdown.setup_cost || 0)}</span>
                      </div>
                      {(artwork.custom_colour_count || 0) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Custom Colour Fee:</span>
                          <span className="font-mono">{formatCurrency((artwork.custom_colour_count || 0) * 60)}</span>
                        </div>
                      )}
                      {(artwork.cuts_per_sheet || 0) > 0 && (() => {
                        const sheetsRequired = breakdown.sheets_required || Math.ceil((breakdown.total_placements || totalPlacements) / (artwork.prints_per_sheet || 1));
                        const cutsPerSheet = artwork.cuts_per_sheet || 0;
                        const guillotineCost = sheetsRequired * cutsPerSheet * 0.10;
                        return (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Guillotine:</span>
                            <span className="font-mono">
                              {formatCurrency(guillotineCost)}
                              <span className="text-xs text-slate-400 ml-1">
                                ({sheetsRequired} × {cutsPerSheet} × $0.10)
                              </span>
                            </span>
                          </div>
                        );
                      })()}
                      <div className="flex justify-between pt-2 border-t border-slate-300 font-bold">
                        <span className="text-slate-700">Total Screen Print Cost:</span>
                        <span className="font-mono text-base">{formatCurrency(breakdown.cost || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Eligibility Warning */}
              {breakdown?.error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Eligibility Issue</p>
                      <p className="text-sm text-amber-700 mt-1">{breakdown.error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* UltraColour Reflective specific fields (sheet-based, mirrors SPP minus sheet size) */}
          {false && artwork.print_method === "UltraColour Reflective" && (
            <div className="pt-3 border-t space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Garments per Sheet</Label>
                  <Input
                    type="number" min="1" max="50"
                    value={artwork.prints_per_sheet || 1}
                    onChange={(e) => onUpdate("prints_per_sheet", Math.max(1, parseInt(e.target.value) || 1))}
                    className="h-9 font-mono"
                    data-testid={`ucr-garments-per-sheet-${index}`}
                  />
                  <p className="text-[11px] text-muted-foreground">Min 20 units. $40 setup per colour.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Colours</Label>
                  <Input
                    type="number" min="1" max="3"
                    value={artwork.screens || 1}
                    onChange={(e) => onUpdate("screens", Math.min(3, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="h-9 font-mono"
                    data-testid={`ucr-colour-count-${index}`}
                  />
                  <p className="text-[11px] text-muted-foreground">Max 3 colours</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    id={`ucr-bw-${index}`}
                    checked={artwork.print_colour === "White" || artwork.print_colour === "Black"}
                    onCheckedChange={(checked) => onUpdate("print_colour", checked ? "Black" : "Other")}
                    data-testid={`ucr-black-white-${index}`}
                  />
                  <Label htmlFor={`ucr-bw-${index}`} className="text-sm cursor-pointer">Black/White Only</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id={`ucr-lod-${index}`}
                    checked={artwork.light_on_dark || false}
                    onCheckedChange={(checked) => onUpdate("light_on_dark", checked)}
                    data-testid={`ucr-light-dark-${index}`}
                  />
                  <Label htmlFor={`ucr-lod-${index}`} className="text-sm cursor-pointer">Light on Dark</Label>
                </div>
              </div>

              {/* Breakdown */}
              {breakdown && !breakdown.error && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Sheets Required{breakdown.sheet_tier ? ` (${breakdown.sheet_tier})` : ""}:</span><span className="font-mono">{breakdown.sheets_required}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Sheet Cost:</span><span className="font-mono">{formatCurrency(breakdown.print_cost || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Setup ({breakdown.effective_colours} × ${breakdown.setup_per_colour || 40}):</span><span className="font-mono">{formatCurrency(breakdown.setup_cost || 0)}</span></div>
                  <div className="flex justify-between pt-1 border-t border-slate-300 font-bold"><span className="text-slate-700">Total:</span><span className="font-mono">{formatCurrency(breakdown.cost || 0)}</span></div>
                </div>
              )}
              {breakdown?.error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-700">{breakdown.error}</p>
                </div>
              )}
            </div>
          )}

          {/* Traditional Screen Print specific fields (CSV-driven) */}
          {false && artwork.print_method === "Traditional Screen Print" && (() => {
            const colourOptions = (tspConfig.colours && tspConfig.colours.length > 0) ? tspConfig.colours : [1, 2, 3, 4, 5, 6];
            const selectedCharges = artwork.tsp_selected_charges ?? ["Film", "Screen"];
            const tspColours = artwork.tsp_colours || 1;
            const toggleCharge = (name, on) => {
              const next = on ? [...new Set([...selectedCharges, name])] : selectedCharges.filter((c) => c !== name);
              onUpdate("tsp_selected_charges", next);
            };
            const setRepeatJob = (on) => {
              // Repeat jobs reuse existing screens/film — waive Film & Screen automatically.
              const waivable = ["Film", "Screen"];
              const next = on
                ? selectedCharges.filter((c) => !waivable.includes(c))
                : [...new Set([...selectedCharges, ...waivable])];
              (onUpdateFields || ((f) => Object.entries(f).forEach(([k, v]) => onUpdate(k, v))))({
                tsp_repeat_job: on,
                tsp_selected_charges: next,
              });
            };
            return (
            <div className="pt-3 border-t space-y-4">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">
                Traditional Screen Print
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Colours</Label>
                  <Select
                    value={String(tspColours)}
                    onValueChange={(v) => {
                      const val = parseInt(v);
                      onUpdate("tsp_colours", val);
                    }}
                  >
                    <SelectTrigger className="h-9" data-testid={`tsp-colour-count-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colourOptions.map((c) => (
                        <SelectItem key={c} value={String(c)}>{c} Colour{c !== 1 ? "s" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">From uploaded matrix</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Repeat Job</Label>
                  <div className="flex items-center gap-2 h-9">
                    <Switch
                      id={`tsp-repeat-${index}`}
                      checked={artwork.tsp_repeat_job || false}
                      onCheckedChange={setRepeatJob}
                      data-testid={`tsp-repeat-job-${index}`}
                    />
                    <Label htmlFor={`tsp-repeat-${index}`} className="text-xs cursor-pointer text-muted-foreground">
                      Waive Film &amp; Screen charges
                    </Label>
                  </div>
                </div>
              </div>

              {/* One-off charges checklist */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">One-Off Job Charges</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {(tspConfig.charges || []).map((charge) => {
                    const name = charge.ChargeName;
                    const ctype = String(charge.ChargeType || "flat").toLowerCase().replace(/_/g, "-").replace(/\s/g, "-");
                    const isPerColour = ctype.includes("colour") || ctype.includes("color");
                    const isPerUnit = ctype.includes("unit") || ctype.includes("piece");
                    const isPerMatch = ctype.includes("match");
                    const matchQty = (artwork.tsp_charge_quantities || {})[name] ?? 1;
                    let amount;
                    if (isPerColour) amount = (charge.Amount || 0) * tspColours;
                    else if (isPerUnit) amount = (charge.Amount || 0) * (totalPlacements || 0);
                    else if (isPerMatch) amount = (charge.Amount || 0) * matchQty;
                    else amount = charge.Amount || 0;
                    const checked = selectedCharges.includes(name);
                    const unitLabel = isPerColour ? "/colour" : isPerUnit ? "/unit" : isPerMatch ? "/match" : "";
                    return (
                      <div
                        key={name}
                        className={`flex items-center justify-between gap-2 rounded border px-2 py-1.5 ${checked ? "bg-blue-50 border-blue-200" : "bg-white"}`}
                        data-testid={`tsp-charge-${index}-${name.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <label className="flex items-center gap-2 min-w-0 cursor-pointer flex-1">
                          <Checkbox checked={checked} onCheckedChange={(v) => toggleCharge(name, !!v)} />
                          <span className="text-sm truncate">{name}</span>
                          {unitLabel && <span className="text-[10px] text-muted-foreground">{unitLabel}</span>}
                        </label>
                        {isPerMatch && checked && (
                          <Input
                            type="number"
                            min="1"
                            value={matchQty}
                            onChange={(e) => {
                              const q = Math.max(1, parseInt(e.target.value) || 1);
                              onUpdate("tsp_charge_quantities", { ...(artwork.tsp_charge_quantities || {}), [name]: q });
                            }}
                            className="h-7 w-14 font-mono text-xs px-1.5"
                            data-testid={`tsp-charge-qty-${index}-${name.replace(/\s+/g, "-").toLowerCase()}`}
                          />
                        )}
                        <span className="font-mono text-xs text-slate-500">{formatCurrency(amount)}</span>
                      </div>
                    );
                  })}
                </div>
                {(tspConfig.charges || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No one-off charges configured. Add them in Settings → Pricing Tables.</p>
                )}
              </div>

              {/* Breakdown */}
              {breakdown && !breakdown.error && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Print ({breakdown.total_placements || totalPlacements} × {formatCurrency(breakdown.price_per_unit || 0)}):</span>
                    <span className="font-mono">{formatCurrency(breakdown.print_cost || 0)}</span>
                  </div>
                  {(breakdown.charge_lines || []).map((c, i) => (
                    <div key={i} className="flex justify-between" data-testid={`tsp-breakdown-charge-${index}-${i}`}>
                      <span className="text-slate-500">{c.name}{c.qty_label ? ` (${c.qty_label})` : ""}:</span>
                      <span className="font-mono">{formatCurrency(c.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-1 border-t border-slate-300 font-bold">
                    <span className="text-slate-700">Total:</span>
                    <span className="font-mono">{formatCurrency(breakdown.cost || 0)}</span>
                  </div>
                </div>
              )}
              {breakdown?.error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-700">{breakdown.error}</p>
                </div>
              )}
            </div>
            );
          })()}

          {/* UltraColour Pro specific fields */}
          {false && artwork.print_method === "UltraColour Pro" && (
            <div className="pt-3 border-t">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">
                UltraColour Pro Settings
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Blocker Type</Label>
                  <Select
                    value={artwork.blocker || ""}
                    onValueChange={(v) => onUpdate("blocker", v)}
                  >
                    <SelectTrigger data-testid={`artwork-uc-pro-blocker-${index}`}>
                      <SelectValue placeholder="Select blocker..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOCKERS.map((blocker) => (
                        <SelectItem key={blocker} value={blocker}>
                          {blocker}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Screen Print Pro Comparison Toggle moved to the unified comparison control below */}
              </div>
            </div>
          )}

          {/* Embroidery specific fields */}
          {false && artwork.print_method === "Embroidery" && (
            <div className="pt-3 border-t">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">
                Embroidery Settings
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Stitch Count</Label>
                  <Input
                    type="number"
                    min="9999"
                    step="1000"
                    placeholder="9999"
                    value={artwork.stitch_count || 9999}
                    onChange={(e) =>
                      onUpdate("stitch_count", parseInt(e.target.value) || 9999)
                    }
                    data-testid={`artwork-stitch-count-${index}`}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Est. Time per Unit (mins)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Minutes per garment"
                    value={artwork.estimated_time_mins || ""}
                    onChange={(e) =>
                      onUpdate("estimated_time_mins", parseInt(e.target.value) || 0)
                    }
                    data-testid={`artwork-emb-time-${index}`}
                    className="font-mono"
                  />
                  <p className="text-[10px] text-muted-foreground">Time to embroider each garment</p>
                </div>
              </div>
            </div>
          )}

          {/* Manual Override specific fields */}
          {false && artwork.print_method === "Manual Override" && (
            <div className="pt-3 border-t">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">
                Manual Override - Custom Pricing
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Enter custom pricing. All fields are optional - fill in what you need.
              </p>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    Sell Price (ex GST)
                    <span className="text-muted-foreground font-normal">- client sees this</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter sell price..."
                    value={artwork.override_sell_ex_gst ?? ""}
                    onChange={(e) =>
                      onUpdate("override_sell_ex_gst", e.target.value ? parseFloat(e.target.value) : null)
                    }
                    data-testid={`artwork-override-sell-${index}`}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Internal Cost (ex GST)
                    <span className="text-muted-foreground font-normal">- for margin calc</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter cost..."
                    value={artwork.override_cost_ex_gst ?? ""}
                    onChange={(e) =>
                      onUpdate("override_cost_ex_gst", e.target.value ? parseFloat(e.target.value) : null)
                    }
                    data-testid={`artwork-override-cost-${index}`}
                    className="font-mono"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Label (optional)</Label>
                  <Input
                    placeholder="e.g., Special embroidery, Custom print..."
                    value={artwork.override_label || ""}
                    onChange={(e) => onUpdate("override_label", e.target.value)}
                    data-testid={`artwork-override-label-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Est. Time (mins) - optional</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Time estimate..."
                    value={artwork.override_time_mins ?? ""}
                    onChange={(e) =>
                      onUpdate("override_time_mins", e.target.value ? parseFloat(e.target.value) : null)
                    }
                    data-testid={`artwork-override-time-${index}`}
                    className="font-mono"
                  />
                </div>
              </div>
              
              {/* Show calculated margin if both sell and cost are entered */}
              {artwork.override_sell_ex_gst > 0 && artwork.override_cost_ex_gst > 0 && (
                <div className="mt-3 p-2 bg-green-50 rounded text-xs">
                  <span className="text-green-700">
                    Margin: {formatCurrency(artwork.override_sell_ex_gst - artwork.override_cost_ex_gst)} 
                    ({(((artwork.override_sell_ex_gst - artwork.override_cost_ex_gst) / artwork.override_sell_ex_gst) * 100).toFixed(1)}%)
                  </span>
                </div>
              )}
              
              {/* Warning if no pricing entered */}
              {!artwork.override_sell_ex_gst && !artwork.override_cost_ex_gst && (
                <div className="mt-3 p-2 bg-amber-50 rounded text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-700">
                    No pricing entered yet. This print group won't affect totals until you add a sell or cost price.
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Unified Method Comparison toggle — consistent across ALL print methods */}
          {!isRetailMode && artwork.print_method && (
            <div className="pt-3 border-t flex flex-wrap items-center gap-3" data-testid={`comparison-row-${index}`}>
              <div className="flex items-center gap-2">
                <Switch
                  id={`compare-${index}`}
                  checked={artwork.comparison_enabled || false}
                  onCheckedChange={(checked) => onUpdate("comparison_enabled", checked)}
                  data-testid={`artwork-compare-toggle-${index}`}
                />
                <Label htmlFor={`compare-${index}`} className="text-xs cursor-pointer">
                  Include in comparison
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">Compare this print group against another decoration method to find the most cost-effective option.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {artwork.comparison_enabled && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">vs</Label>
                  <Select
                    value={artwork.comparison_target || ""}
                    onValueChange={(v) => onUpdate("comparison_target", v)}
                  >
                    <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`artwork-compare-method-${index}`}>
                      <SelectValue placeholder="Select method..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PRINT_METHODS.filter((m) => m !== artwork.print_method).map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Notes field */}
          <div className="pt-3 border-t space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input
              placeholder="Any special instructions for this artwork..."
              value={artwork.notes || ""}
              onChange={(e) => onUpdate("notes", e.target.value)}
              data-testid={`artwork-notes-${index}`}
            />
          </div>

          {/* Internal Breakdown (Admin only) */}
          {showInternal && breakdown && (
            <div className="pt-3 border-t">
              <div className="p-3 bg-slate-100 rounded-lg space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-slate-500" />
                  <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Internal Breakdown
                  </Label>
                </div>
                {/* Screen Print Pro detailed breakdown */}
                {artwork.print_method === "Screen Print Pro" && (
                  <div className="space-y-1">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <div>
                        <span className="text-slate-500">Placements:</span>{" "}
                        <span className="font-mono">{breakdown.total_placements || 0}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Prints/Sheet:</span>{" "}
                        <span className="font-mono">{artwork.prints_per_sheet || 1}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Sheets Required:</span>{" "}
                        <span className="font-mono">{breakdown.sheets_required || Math.ceil((breakdown.total_placements || 0) / (artwork.prints_per_sheet || 1))}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Colours:</span>{" "}
                        <span className="font-mono">{artwork.screens || 1}</span>
                      </div>
                    </div>
                    <div className="pt-2 mt-2 border-t border-slate-200 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Print Total:</span>
                        <span className="font-mono">{formatCurrency(breakdown.print_cost || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Setup Total ({artwork.screens || 1} × ${breakdown.setup_per_colour || 0}/colour):</span>
                        <span className="font-mono">{formatCurrency(breakdown.setup_cost || 0)}</span>
                      </div>
                      {artwork.custom_colour_enabled && artwork.custom_colour_count > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Custom Colour ({artwork.custom_colour_count} × $60):</span>
                          <span className="font-mono">{formatCurrency(artwork.custom_colour_count * 60)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-slate-300 font-bold">
                        <span className="text-slate-700">Final Total:</span>
                        <span className="font-mono text-base">{formatCurrency(breakdown.cost || 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* Generic breakdown for other methods */}
                {artwork.print_method !== "Screen Print Pro" && (() => {
                  const placements = breakdown.total_placements ?? breakdown.placements ?? 0;
                  const cost = breakdown.cost || 0;
                  const costPerPlacement = breakdown.cost_per_placement ?? (placements > 0 ? cost / placements : 0);
                  return (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-slate-500">Method:</span>{" "}
                      <span className="font-medium">{breakdown.method}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Placements:</span>{" "}
                      <span className="font-mono">{placements}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Cost/placement:</span>{" "}
                      <span className="font-mono">{formatCurrency(costPerPlacement)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Total Cost:</span>{" "}
                      <span className="font-mono font-bold">{formatCurrency(cost)}</span>
                    </div>
                  </div>
                  );
                })()}
                {breakdown.error && (
                  <div className="flex items-center gap-1 text-destructive bg-destructive/10 p-2 rounded">
                    <AlertCircle className="w-4 h-4" />
                    <span>{breakdown.error}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ArtworkGroupCard;
